// One subject file: session timeline + longitudinal progress (the demo money
// view). Sessions and analytics are server-filtered by the caller's visibility.
import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Divider, Stack, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate, useParams } from 'react-router-dom';
import lpsService from '../services/lpsService';
import SessionAnalytics from '../components/SessionAnalytics';
import { nounsFor } from '../utils/verticalNouns';

const fmtDate = (iso) => (iso || '').slice(0, 16).replace('T', ' ');
const fmtDur = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}` : '—');

export default function SubjectDetailPage() {
  const { subjectId } = useParams();
  const [detail, setDetail] = useState(null);
  const [progress, setProgress] = useState(null);
  const [nouns, setNouns] = useState(nounsFor('general'));
  const navigate = useNavigate();

  useEffect(() => {
    lpsService.getSubject(subjectId).then(setDetail).catch(() => setDetail({ sessions: [] }));
    lpsService.getLongitudinal(subjectId)
      .then((d) => setProgress(d.sessions || [])).catch(() => setProgress([]));
    lpsService.getVertical().then((v) => setNouns(nounsFor(v.vertical))).catch(() => {});
  }, [subjectId]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/subjects')}>
          {nouns.subjects}
        </Button>
        <Typography variant="h5" fontWeight={700}>
          {nouns.subject} {subjectId}
        </Typography>
      </Stack>

      {progress && progress.length >= 2 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Progress</Typography>
          <SessionAnalytics longitudinal={progress}
            lang={(progress[0]?.locale || '').startsWith('he') ? 'he' : 'en'} />
        </Box>
      )}

      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
        {nouns.sessions} ({detail?.sessions?.length ?? '…'})
      </Typography>
      <Stack spacing={1.5}>
        {(detail?.sessions || []).slice().reverse().map((s) => (
          <Card variant="outlined" key={s.id}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={600}>{s.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtDate(s.created_at)} · {fmtDur(s.duration_seconds)} · {s.department}
                  </Typography>
                </Box>
                <Chip size="small" label={s.status}
                  color={s.status === 'done' ? 'success' : s.status === 'failed' ? 'error' : 'info'} />
                {s.has_analytics && <Chip size="small" variant="outlined" label="analytics" />}
                <Button size="small" startIcon={<DescriptionIcon />} disabled={s.status !== 'done'}
                  onClick={() => navigate(`/recordings/${s.id}`)}>
                  Open
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
      {progress && progress.length < 2 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Progress trends appear once this {nouns.subject.toLowerCase()} has two or more
            completed {nouns.sessions.toLowerCase()} with analytics.
          </Typography>
        </>
      )}
    </Box>
  );
}
